import React, { useState, useEffect, useCallback } from "react";
import CreateTheme from "../createTheme/createTheme";
import { useDispatch, useSelector } from "react-redux";
import { fetchThemeData } from "../../themeSlice";
import { useNavigate } from "react-router-dom";

import Swal from "sweetalert2";
import "./home.css";

import { selectAdminToken, selectSessionId } from "../../sessionSlice";
import UpdateThemeImages from "../updateThemeImage/updateThemeImage";

function Home() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [themes, setThemes] = useState([]);
  const [themesLoading, setThemesLoading] = useState(false);
  const adminToken = useSelector(selectAdminToken);
  const sessionId = useSelector(selectSessionId);
  const isSuperadmin = sessionId === "admin";
  const navigate = useNavigate();

  const dispatch = useDispatch();
  const { status, data } = useSelector((state) => state.theme);

  // Load initial selected theme preview (do not block the themes list on this)
  useEffect(() => {
    if (!isUpdateModalOpen) {
      dispatch(fetchThemeData({ isAdmin: true }));
    }
  }, [dispatch, isUpdateModalOpen]);

  const browseTheme = async (themeName) => {
    try {
      await dispatch(fetchThemeData({ themeId: themeName })).unwrap();
      navigate("/admin/rules");
    } catch (err) {
      Swal.fire("Error loading theme", err.message, "error");
    }
  };

  const fetchAllThemes = useCallback(() => {
    if (!adminToken) return;
    setThemesLoading(true);
    fetch(process.env.REACT_APP_BACKEND_URL + "/fetchAllThemes", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${adminToken}`,
      },
      body: JSON.stringify({
        test: "test",
      }),
    })
      .then((res) => {
        if (!res.ok) throw new Error("Network response was not ok");
        return res.json();
      })
      .then((json) => {
        if (json.success) {
          setThemes(Array.isArray(json.data) ? json.data : []);
        } else {
          console.error("API error:", json.message);
        }
      })
      .catch((err) => console.error("Fetch error:", err))
      .finally(() => setThemesLoading(false));
  }, [adminToken]);

  useEffect(() => {
    if (adminToken) {
      fetchAllThemes();
    }
  }, [adminToken, fetchAllThemes]);

  const selectThemeOnServer = (themeName) => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/selectTheme`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${adminToken}`,
      },
      body: JSON.stringify({
        themeName,
      }),
    })
      .then((res) => res.json())
      .then((json) => {
        if (!json.success) {
          console.error("SelectTheme error:", json.message);
        } else {
          Swal.fire({
            title: "Theme Selected",
            text: "",
            icon: "success",
          });
          fetchAllThemes();
          dispatch(fetchThemeData({ isAdmin: true }));
        }
      })
      .catch((err) => console.error("SelectTheme fetch error:", err));
  };

  const deleteTheme = (theme, e) => {
    e?.stopPropagation?.();
    if (!theme?.canDelete) {
      Swal.fire(
        "Cannot delete",
        "Superadmin themes cannot be deleted from a session. Customize them for this session instead, or delete only themes you created here.",
        "warning"
      );
      return;
    }
    Swal.fire({
      title: "Are you sure?",
      text: `Delete "${theme.displayName || theme.themename}"? This only affects ${
        isSuperadmin ? "all sessions (superadmin theme)" : "this session"
      }.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, keep it",
    }).then((result) => {
      if (result.isConfirmed) {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/deleteTheme`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({ themeName: theme.themename }),
        })
          .then((res) => res.json())
          .then((json) => {
            if (!json.success) {
              console.error("deleteTheme error:", json.message);
              Swal.fire("Error", json.message, "error");
            } else {
              Swal.fire("Deleted!", `Theme has been deleted.`, "success").then(
                () => {
                  fetchAllThemes();
                  dispatch(fetchThemeData({ isAdmin: true }));
                }
              );
            }
          })
          .catch((err) => {
            console.error("deleteTheme fetch error:", err);
            Swal.fire("Error", "Network error, please try again.", "error");
          });
      }
    });
  };

  const OpenUpdateModal = (themeName) => {
    dispatch(fetchThemeData({ themeId: themeName }));
    setIsUpdateModalOpen(true);
  };

  const previewLoading = status === "loading" && !data;
  const previewFailed = status === "failed" && !data;

  return (
    <>
      <CreateTheme
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onCreated={fetchAllThemes}
      />
      <UpdateThemeImages
        isOpen={isUpdateModalOpen}
        onClose={() => setIsUpdateModalOpen(false)}
      />
      <div className="te-container">
        <div className="te-left">
          {previewLoading ? (
            <div className="load-theme-container">
              <div className="load-theme-spinner"></div>
              <p>Loading theme...</p>
            </div>
          ) : previewFailed ? (
            <div className="load-theme-container">
              <p className="load-theme-error">❌ Error loading theme</p>
            </div>
          ) : (
            <>
              <div className="te-image-wrapper">
                {data?.themename ? (
                  <img
                    src={process.env.REACT_APP_S3_PATH + `${data?.themeImage}`}
                    className="te-main-image"
                    alt={data?.themeName || "Theme"}
                  />
                ) : (
                  <>No Theme Found</>
                )}
              </div>

              <div className="te-info">
                <h2 className="te-name">{data?.themeName}</h2>
                <p className="te-desc">{data?.themeDescription}</p>
              </div>
            </>
          )}
        </div>

        <div className="te-right">
          <div className="te-select-header">
            <h3 className="te-select-title">Select Themes</h3>
            <button className="te-create-btn" onClick={() => setIsModalOpen(true)}>
              + Create Theme
            </button>
          </div>
          {!isSuperadmin ? (
            <p className="te-list-hint">
              Superadmin themes + themes created in this session only.
            </p>
          ) : null}
          <div className="te-themes-list">
            {themesLoading && themes.length === 0 ? (
              <p style={{ padding: 12, opacity: 0.7 }}>Loading themes...</p>
            ) : null}
            {themes.map((theme) => (
              <div
                key={theme.themename || theme.id}
                className="te-theme-item"
                onClick={() => selectThemeOnServer(theme.themename)}
              >
                <img
                  src={process.env.REACT_APP_S3_PATH + `${theme.themeImage}`}
                  alt={theme.displayName || theme.themename}
                  className="te-theme-image"
                />

                {theme.selected === "true" && (
                  <i className="fa-solid fa-check te-selected-icon" title="Selected" />
                )}

                <div className="te-theme-caption" title={theme.displayName}>
                  {theme.displayName || theme.themename}
                </div>

                <div className="te-icons">
                  <i
                    className="fa-solid fa-image te-icon"
                    title="Edit Image (this session)"
                    onClick={(e) => {
                      e.stopPropagation();
                      OpenUpdateModal(theme.themename);
                    }}
                  ></i>
                  <i
                    className="fa-solid fa-gear te-icon"
                    title="Settings (this session)"
                    onClick={(e) => {
                      e.stopPropagation();
                      browseTheme(theme.themename);
                    }}
                  />
                  {theme.canDelete ? (
                    <i
                      className="fa-solid fa-trash te-icon"
                      title="Delete"
                      onClick={(e) => deleteTheme(theme, e)}
                    ></i>
                  ) : null}
                </div>
              </div>
            ))}
            {!themesLoading && themes.length === 0 ? (
              <p style={{ padding: 12, opacity: 0.7 }}>No themes yet.</p>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default Home;
